import { ChangeDetectionStrategy, Component, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ISkyUiPopoverOptions } from '../../interface';
import { fromEvent, EMPTY } from 'rxjs';
import { filter, withLatestFrom, switchMap, map, startWith, distinctUntilChanged } from 'rxjs/operators';
import { returnToAngularZone, DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { trigger, transition, style, animate } from '@angular/animations';
import { Store } from '@skyeng/libs/shared/store';

export const ANIMATION_DURATION_MS = 100;

const STORE_INITIAL_STATE = {
  state: 'hidden' as 'visible' | 'closed' | 'hidden',
  options: {} as ISkyUiPopoverOptions,
  focused: true,
};

interface StoreEvents {
  closed: void;
}

@DecorateUntilDestroy()
@Component({
  selector: 'sky-ui-popover',
  templateUrl: 'popover.html',
  styleUrls: ['popover.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('displayAnimation', [
      transition('* => visible', [style({ opacity: 0 }), animate(ANIMATION_DURATION_MS)]),
      transition('* => closed', [animate(ANIMATION_DURATION_MS, style({ opacity: 0 }))]),
    ]),
  ],
})
export class SkyUiPopoverComponent implements OnInit, OnDestroy {
  private store = new Store<typeof STORE_INITIAL_STATE, StoreEvents>(STORE_INITIAL_STATE);

  closed$ = this.store.on('closed');
  options$ = this.store.select('options');
  state$ = this.store.select('state');
  focused$ = this.store.select('focused');

  constructor(private ngZone: NgZone, private elementRef: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(document, 'click', { capture: true })
        .pipe(
          map(event => event.target as Node),
          withLatestFrom(this.options$),
          filter(
            ([target, options]) =>
              document.contains(target) &&
              !this.elementRef.nativeElement.contains(target) &&
              !(target as HTMLElement).closest('.cdk-overlay-container') &&
              !!options.closeOnClickOutside,
          ),
          returnToAngularZone(this.ngZone),
          takeUntilDestroyed(this),
        )
        .subscribe(() => this.close());

      const focused$ = fromEvent(document, 'mousedown', { capture: true }).pipe(
        map(event => event.target as Node),
        map(target => this.elementRef.nativeElement.contains(target)),
        startWith(true),
        distinctUntilChanged(),
      );

      this.options$
        .pipe(
          switchMap(({ blur }) => (blur ? focused$ : EMPTY)),
          returnToAngularZone(this.ngZone),
          takeUntilDestroyed(this),
        )
        .subscribe(focused => this.store.updateFields({ focused }));
    });
  }

  ngOnDestroy(): void {}

  setOptions(options: ISkyUiPopoverOptions): void {
    this.store.updateFields({ options });
  }

  close(): void {
    this.store.updateFields({ state: 'closed' });
  }

  onDisplayAnimationDone(): void {
    if (this.store.get('state') === 'closed') {
      this.store.fire('closed');
    }
  }

  onPopoverReady(): void {
    if (this.store.get('state') === 'hidden') {
      this.store.updateField('state', 'visible');
    }
  }
}
